import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useAuthStore } from '@carouselgg/auth/store'
import { Avatar } from '@carouselgg/nexus/avatar'
import { Badge } from '@carouselgg/nexus/badge'
import { Button } from '@carouselgg/nexus/button'
import { Card } from '@carouselgg/nexus/card'
import { Text } from '@carouselgg/nexus/text'
import { toast } from '@carouselgg/nexus/toast'
import { CircleCheck, MapPin, QrCode as QrIcon, UserPlus, Users, Shield } from '@carouselgg/nexus/icon'
import { ChoiceList, Choice } from '@carouselgg/nexus/choice-list'
import { applyToTeam, checkIn, getCheckInLink, listDoorTeams, type CheckInLink, type DoorTeam } from '../api/checkin'
import { closedMessage, doorLink, returnTo, venueLine } from './checkInCopy'
import styles from './CheckInPage.module.css'

type Load = { kind: 'loading' } | { kind: 'invalid' } | { kind: 'ready'; link: CheckInLink }

/**
 * What a venue's check-in QR code opens. A registered player checks in with one
 * tap; someone who isn't registered yet gets the ways in that are still open —
 * register a team, join one, or sign up as a free agent. Works without the app:
 * a phone's own camera lands here.
 */
export function CheckInPage() {
  const { token = '' } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isAuthLoading = useAuthStore((s) => s.isLoading)

  const [load, setLoad] = useState<Load>({ kind: 'loading' })
  const [checkingIn, setCheckingIn] = useState(false)
  const [teams, setTeams] = useState<DoorTeam[] | null>(null)
  const [appliedTo, setAppliedTo] = useState<string | null>(null)

  const here = `/check-in/${token}`

  const refresh = useCallback(async () => {
    try {
      setLoad({ kind: 'ready', link: await getCheckInLink(token) })
    } catch {
      setLoad({ kind: 'invalid' })
    }
  }, [token])

  // Re-read once auth settles: the same link answers differently for a
  // signed-in player (their state) than for a stranger.
  useEffect(() => {
    if (isAuthLoading) return
    void refresh()
  }, [refresh, isAuthLoading, isAuthenticated])

  async function handleCheckIn() {
    setCheckingIn(true)
    try {
      const res = await checkIn(token)
      toast.success(res.already_checked_in ? "You're already checked in." : "You're checked in!")
      await refresh()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Check-in failed')
    } finally {
      setCheckingIn(false)
    }
  }

  async function showTeams() {
    try {
      setTeams(await listDoorTeams(token))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not load teams')
    }
  }

  async function handleApply(link: CheckInLink, team: DoorTeam) {
    try {
      await applyToTeam(link.season.org.id, team.team_id, link.season.id)
      setAppliedTo(team.name)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send the request')
    }
  }

  if (load.kind === 'loading') {
    return <div className={styles.page}><Text muted>Loading check-in…</Text></div>
  }

  if (load.kind === 'invalid') {
    return (
      <div className={styles.page}>
        <Card>
          <div className={styles.content}>
            <Text as="h1" size="xl" weight="bold">This code doesn't work</Text>
            <Text muted>It may have been replaced with a new one. Scan the code at the venue again, or ask the desk.</Text>
            <Button variant="primary" onClick={() => navigate('/')}>Go home</Button>
          </div>
        </Card>
      </div>
    )
  }

  const { link } = load
  const { season, me } = link
  const venue = venueLine(season)
  const canCheckIn = link.open && me !== null && me.state !== 'not_registered' && me.state !== 'checked_in'
  const anyDoor = link.door.register_team || link.door.join_team || link.door.free_agent

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.content}>
          {season.org.logo_url && <img src={season.org.logo_url} alt="" className={styles.orgLogo} />}
          <Text size="sm" muted>{season.org.name}</Text>
          <Text as="h1" size="xl" weight="bold">{season.name}</Text>
          {venue && (
            <Text size="sm" muted className={styles.venue}><MapPin size={14} /> {venue}</Text>
          )}

          {me?.state === 'checked_in' && (
            <div className={styles.done}>
              <CircleCheck size={40} className={styles.doneIcon} />
              <Text size="lg" weight="semibold">You're checked in</Text>
              {me.team && <Text muted>Playing for {me.team.name}</Text>}
            </div>
          )}

          {!link.open && me?.state !== 'checked_in' && (
            <Text muted>{closedMessage(link.closed_reason)}</Text>
          )}

          {me === null && (
            <>
              <Text size="sm" muted>Sign in to check in. New here? Create an account first — it only takes a minute.</Text>
              <div className={styles.actions}>
                <Button variant="primary" onClick={() => navigate(`/login${returnTo(here)}`)}>Sign in</Button>
                <Button variant="secondary" onClick={() => navigate(`/signup${returnTo(here)}`)}>Create account</Button>
              </div>
            </>
          )}

          {canCheckIn && me && (
            <>
              {me.team && (
                <Text>
                  You're on <strong>{me.team.name}</strong>
                  {me.state === 'pending_team' && <Badge variant="warning" size="sm" className={styles.inlineBadge}>Awaiting approval</Badge>}
                  {me.state === 'awaiting_payment' && <Badge variant="warning" size="sm" className={styles.inlineBadge}>Awaiting payment</Badge>}
                </Text>
              )}
              {me.state === 'awaiting_payment' && (
                <Text>Your team hasn't paid its entry yet. Pay at the desk — once staff mark your team paid, you can check in.</Text>
              )}
              {me.state === 'free_agent' && <Text>You're signed up as a free agent — staff will place you on a team.</Text>}
              {/* The pass is the front door: staff scan it and see who you are. */}
              <Text size="sm" muted>Show your pass at the desk — staff scan it to check you in.</Text>
              <Button
                variant={link.self_check_in ? 'secondary' : 'primary'}
                size="lg"
                className={styles.checkIn}
                onClick={() => navigate('/passes')}
              >
                <QrIcon size={18} /> Show my pass
              </Button>
              {/* The server would refuse it until the team has paid. */}
              {link.self_check_in && me.state !== 'awaiting_payment' && (
                <Button variant="primary" size="lg" onClick={() => void handleCheckIn()} disabled={checkingIn} className={styles.checkIn}>
                  {checkingIn ? 'Checking in…' : "I'm here — check in"}
                </Button>
              )}
            </>
          )}

          {me?.state === 'not_registered' && (
            <div className={styles.door}>
              <Text weight="semibold">You're not registered for this tournament</Text>
              {anyDoor ? (
                <>
                  <Text size="sm" muted>You can still get in at the door:</Text>
                  {appliedTo ? (
                    <Text size="sm">Request sent to <strong>{appliedTo}</strong>. Once their captain or the desk approves it, scan the code again to check in.</Text>
                  ) : teams ? (
                    <ul className={styles.teamList}>
                      {teams.length === 0 && <Text size="sm" muted>No team has room right now.</Text>}
                      {teams.map((t) => (
                        <li key={t.team_id} className={styles.teamRow}>
                          <Avatar src={t.logo_url} fallback={t.name[0] ?? '?'} size="sm" />
                          <Text size="sm" className={styles.teamName}>{t.name}</Text>
                          <Text size="xs" muted>{t.members}/{t.capacity}</Text>
                          <Button size="sm" variant="secondary" onClick={() => void handleApply(link, t)}>Ask to join</Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ChoiceList aria-label="How do you want to enter?">
                      {link.door.register_team && (
                        <Choice icon={<Shield size={18} />} label="Register a team" hint="You'll be the captain"
                          to={doorLink(season, 'register', here)} />
                      )}
                      {link.door.join_team && (
                        <Choice icon={<Users size={18} />} label="Join a team" hint="Ask a registered team for a spot"
                          onClick={() => void showTeams()} />
                      )}
                      {link.door.free_agent && (
                        <Choice icon={<UserPlus size={18} />} label="Join as a free agent" hint="Staff place you on a team"
                          to={doorLink(season, 'free-agent', here)} />
                      )}
                    </ChoiceList>
                  )}
                </>
              ) : (
                <Text size="sm" muted>Registration is closed. Talk to the desk — staff can still add you.</Text>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
